<script lang="ts">
import type { IconClass } from '#core/types'
import { mobileSocialLinks } from '#core/utils/external-links'
import { ConsultationSourceEnum } from '@forgd/contract/types'
import { Alert, AlertDescription, AlertTitle } from '@forgd/ui-kit/alert'
import { isNavTargetActive } from '@forgd/ui-kit/blocks/nav'
import { Bars3Icon, CalendarDaysIcon, ComputerDesktopIcon, XMarkIcon } from '@heroicons/vue/24/outline'
import { breakpointsTailwind, useBreakpoints, useScrollLock } from '@vueuse/core'

export interface PrMobileHeaderLink {
  label: string
  to: string
  heroIcon?: IconClass
  imageIcon?: string
  shouldHighlight?: (path: string) => boolean
}
</script>

<script lang="ts" setup>
defineProps<{
  title?: string
  icon?: string
  iconClass?: IconClass
  links: PrMobileHeaderLink[]
}>()

const open = ref(false)
const route = useRoute()
const { destination: consultationDestination } = useConsultationDestination()
const { captureConsultationClick } = useConsultationTracking()

function isActive(link: PrMobileHeaderLink): boolean {
  return isNavTargetActive(route.path, { ...link, exact: true })
}

function onConsultationClick() {
  captureConsultationClick(ConsultationSourceEnum.PublicResearchMobileHeader, consultationDestination.value.to)
  open.value = false
}

watch(() => route.fullPath, () => {
  open.value = false
})

const breakpoints = useBreakpoints(breakpointsTailwind)
const isDesktop = breakpoints.greaterOrEqual('md')
watch(isDesktop, (value) => {
  if (value) {
    open.value = false
  }
})

const isScrollLocked = useScrollLock(import.meta.client ? document.body : null)
watch(open, (value) => {
  isScrollLocked.value = value
})
onBeforeUnmount(() => {
  isScrollLocked.value = false
})

const socials = mobileSocialLinks
</script>

<template>
  <div data-el="PrMobileHeader" class="md:hidden sticky top-0 left-0 right-0 z-40 flex h-[60px] w-full border-b border-default bg-neutral-200">
    <NuxtLink
      to="/"
      class="flex items-center justify-center h-full px-4 border-r border-default text-primal"
      aria-label="Home"
    >
      <i class="i-forgd-logo size-6 shrink-0" aria-hidden="true" />
    </NuxtLink>
    <div class="flex flex-1 items-center justify-between min-w-0 px-4">
      <div class="flex items-center gap-2 min-w-0">
        <i v-if="iconClass" :class="iconClass" class="size-6 shrink-0" aria-hidden="true" />
        <img v-else-if="icon" :src="icon" :alt="title" class="h-8 w-8 shrink-0">
        <span class="text-xs font-semibold text-primal truncate">{{ title }}</span>
      </div>
      <button
        type="button"
        class="shrink-0 flex items-center justify-center size-5 text-primal"
        :aria-label="open ? 'Close navigation' : 'Open navigation'"
        :aria-expanded="open"
        @click="open = !open"
      >
        <XMarkIcon v-if="open" class="size-5 shrink-0" aria-hidden="true" />
        <Bars3Icon v-else class="size-5 shrink-0" aria-hidden="true" />
      </button>
    </div>
  </div>

  <ClientOnly>
    <Teleport to="body">
      <Transition
        enter-active-class="transform transition ease-in-out duration-300"
        leave-active-class="transform transition ease-in-out duration-200"
        enter-from-class="translate-x-full"
        leave-to-class="translate-x-full"
      >
        <div
          v-if="open"
          class="fixed inset-x-0 top-15 bottom-0 z-sticky-app md:hidden bg-neutral-200 font-display overflow-y-auto will-change-transform flex flex-col"
          role="dialog"
          aria-modal="true"
        >
          <nav v-if="links.length" class="flex flex-col gap-4 p-4 border-b border-default" aria-label="Primary">
            <NuxtLink
              v-for="item in links"
              :key="item.label"
              :to="item.to"
              :aria-current="isActive(item) ? 'page' : undefined"
              class="flex items-center gap-2 p-2 pr-5 h-12 rounded-sm border border-default bg-neutral-200"
              @click="open = false"
            >
              <i
                v-if="item.heroIcon"
                :class="item.heroIcon"
                class="size-6 shrink-0 text-primal"
                aria-hidden="true"
              />
              <img
                v-else-if="item.imageIcon"
                :src="item.imageIcon"
                :alt="item.label"
                class="size-6"
              >
              <span class="flex-1 text-sm font-semibold text-primal truncate">{{ item.label }}</span>
              <CoreNavActiveDot v-if="isActive(item)" />
            </NuxtLink>
          </nav>

          <div class="flex flex-col gap-4 p-4 border-b border-default">
            <NuxtLink
              :to="consultationDestination.to"
              :external="consultationDestination.external"
              :target="consultationDestination.target"
              :rel="consultationDestination.external ? 'noopener noreferrer' : undefined"
              class="flex items-center gap-2 p-2 pr-5 h-12 rounded-sm border border-default bg-neutral-200"
              @click="onConsultationClick"
            >
              <CalendarDaysIcon class="size-6 shrink-0 text-primal" aria-hidden="true" />
              <span class="flex-1 text-sm font-semibold text-primal truncate">Schedule a Consultation</span>
            </NuxtLink>
          </div>

          <div class="p-4 border-b border-default">
            <Alert class="text-xs">
              <ComputerDesktopIcon aria-hidden="true" />
              <AlertTitle>Experience Forgd Research in a larger screen</AlertTitle>
              <AlertDescription>
                For a more seamless experience and better data visualization, visit Forgd on a large screen.
              </AlertDescription>
            </Alert>
          </div>

          <div class="flex items-center justify-center gap-9 py-5">
            <a
              v-for="s in socials"
              :key="s.label"
              :href="s.href"
              target="_blank"
              rel="noopener noreferrer"
              :aria-label="s.label"
            >
              <img :src="s.icon" :alt="s.label" class="size-4">
            </a>
          </div>
        </div>
      </Transition>
    </Teleport>
  </ClientOnly>
</template>
